/**
 * DataPointsPanel (Sprint 4.3.3, PROD-652).
 *
 * Left-rail entry that lists Data Point Definitions (DPDs) alongside
 * the existing Hierarchy + Activity-types sections in the
 * `<PathwayPalette>`. The panel surfaces the design-time data-point
 * catalogue so forms + calculations have an explicit link surface to
 * reference DPDs by id.
 *
 * Spec sections:
 *   - Closed category enum: lab, vital, demographic, computed,
 *     patient-reported, derived
 *   - Each row renders title + key + value_type, with the closed
 *     category as a tag
 *   - Filter by category (single-select)
 *   - Click-into a row opens a detail view (inline disclosure within
 *     the panel; the detail view is the same-rail expansion).
 *
 * Reference (vault):
 *   03-knowledge-base/external-references/awell/orchestration/
 *   api-reference/overview/domain-model.md
 *   03-knowledge-base/domains/clinical/pathway-engineering/
 *   data-point-definition-vs-data-point.md
 *
 * HC2 token-only.
 * HC5: zero em / en dashes anywhere.
 * HC6 a11y: filter is a labelled `<select>`; rows are `<button>`s with
 * `aria-expanded` driving the disclosure.
 */
import { useId, useMemo, useState } from 'react';
import {
  DATA_POINT_CATEGORIES,
  type DataPointCategory,
  type DataPointDefinition,
} from '../../types/data-point-definition';
import { SAMPLE_DATA_POINT_DEFINITIONS } from '../../data/data-point-definitions';

const ANY_CATEGORY = '__any__';

const CATEGORY_LABELS: Record<DataPointCategory, string> = {
  lab: 'Lab',
  vital: 'Vital',
  demographic: 'Demographic',
  computed: 'Computed',
  'patient-reported': 'Patient-reported',
  derived: 'Derived',
};

export interface DataPointsPanelProps {
  /**
   * Optional override for the catalogue. Defaults to the seeded
   * `SAMPLE_DATA_POINT_DEFINITIONS` fixture; tests inject smaller sets
   * to keep the assertion surface focused.
   */
  definitions?: ReadonlyArray<DataPointDefinition>;
  className?: string;
}

export function DataPointsPanel({
  definitions = SAMPLE_DATA_POINT_DEFINITIONS,
  className,
}: DataPointsPanelProps) {
  const filterId = useId();
  const [categoryFilter, setCategoryFilter] = useState<string>(ANY_CATEGORY);
  const [openDpdId, setOpenDpdId] = useState<string | null>(null);

  const visible = useMemo(() => {
    if (categoryFilter === ANY_CATEGORY) return definitions;
    return definitions.filter((d) => d.category === categoryFilter);
  }, [definitions, categoryFilter]);

  return (
    <section
      data-testid="data-points-panel"
      data-category-filter={categoryFilter}
      className={[
        'flex flex-col gap-gutter-sm border-t border-surface-border pt-gutter-md',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <h4 className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary">
        Data Points
      </h4>
      <p className="text-signal-xs text-text-tertiary">
        Atomic data elements forms + calculations reference by id.
      </p>
      <div className="flex items-center gap-1">
        <label
          htmlFor={filterId}
          className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
        >
          Category
        </label>
        <select
          id={filterId}
          data-testid="data-points-panel-filter-category"
          value={categoryFilter}
          onChange={(e) => {
            setCategoryFilter(e.target.value);
            setOpenDpdId(null);
          }}
          className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
        >
          <option value={ANY_CATEGORY}>Any</option>
          {DATA_POINT_CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>
              {CATEGORY_LABELS[cat]}
            </option>
          ))}
        </select>
      </div>
      {visible.length === 0 ? (
        <p
          data-testid="data-points-panel-empty"
          className="text-signal-xs text-text-tertiary"
        >
          No data points in this category.
        </p>
      ) : (
        <ul
          role="list"
          aria-label="Data point definitions"
          data-testid="data-points-panel-list"
          data-count={visible.length}
          className="flex flex-col gap-1"
        >
          {visible.map((dpd) => (
            <li key={dpd.id}>
              <DataPointRow
                dpd={dpd}
                open={openDpdId === dpd.id}
                onToggle={() =>
                  setOpenDpdId((prev) => (prev === dpd.id ? null : dpd.id))
                }
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

interface DataPointRowProps {
  dpd: DataPointDefinition;
  open: boolean;
  onToggle: () => void;
}

function DataPointRow({ dpd, open, onToggle }: DataPointRowProps) {
  const detailId = `data-points-panel-detail-${dpd.id}`;
  return (
    <div
      data-testid={`data-points-panel-row-${dpd.id}`}
      data-dpd-id={dpd.id}
      data-dpd-category={dpd.category}
      data-dpd-value-type={dpd.value_type}
      className="flex flex-col gap-1 rounded-signal-md border border-surface-border bg-surface-base px-gutter-md py-gutter-sm"
    >
      <button
        type="button"
        data-testid={`data-points-panel-toggle-${dpd.id}`}
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={detailId}
        className="flex w-full flex-col items-start gap-0.5 text-left text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
      >
        <span className="flex items-center gap-1">
          <span className="font-semibold text-text-primary">{dpd.title}</span>
          <span
            data-testid={`data-points-panel-row-category-${dpd.id}`}
            className="rounded-signal-pill bg-surface-muted px-gutter-sm py-0.5 text-signal-xs uppercase tracking-wider text-text-secondary ring-1 ring-surface-border"
          >
            {CATEGORY_LABELS[dpd.category]}
          </span>
        </span>
        <span className="text-signal-xs text-text-tertiary">
          {dpd.key} | {dpd.value_type}
          {dpd.unit !== undefined ? ` | ${dpd.unit}` : ''}
        </span>
      </button>
      {open ? (
        <div
          id={detailId}
          data-testid={`data-points-panel-detail-${dpd.id}`}
          className="flex flex-col gap-0.5 border-t border-surface-border pt-gutter-sm text-signal-xs text-text-secondary"
        >
          <DetailRow label="Id" value={dpd.id} />
          <DetailRow label="Key" value={dpd.key} />
          <DetailRow
            label="Category"
            value={CATEGORY_LABELS[dpd.category]}
          />
          <DetailRow label="Value type" value={dpd.value_type} />
          {dpd.range !== undefined ? (
            <DetailRow
              label="Range"
              value={`${dpd.range.min} to ${dpd.range.max}${
                dpd.unit !== undefined ? ' ' + dpd.unit : ''
              }`}
            />
          ) : null}
          {dpd.possible_values !== undefined ? (
            <DetailRow
              label="Possible values"
              value={dpd.possible_values
                .map((pv) => `${pv.label} (${pv.value})`)
                .join(', ')}
            />
          ) : null}
          {dpd.loinc_code !== undefined ? (
            <DetailRow label="LOINC code" value={dpd.loinc_code} />
          ) : null}
          {dpd.fhir_primary !== undefined ? (
            <DetailRow label="FHIR resource" value={dpd.fhir_primary} />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

interface DetailRowProps {
  label: string;
  value: string;
}

function DetailRow({ label, value }: DetailRowProps) {
  return (
    <div className="flex flex-col">
      <span className="font-semibold uppercase tracking-wide text-text-tertiary">
        {label}
      </span>
      <span className="text-text-secondary">{value}</span>
    </div>
  );
}

export { CATEGORY_LABELS as DATA_POINT_CATEGORY_LABELS };
